import React, { useState, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * Reusable PasswordInput component with built-in Show/Hide toggle.
 * 
 * - By default, password remains hidden (type="password").
 * - Clicking the eye icon toggles between visible (type="text") and hidden.
 * - Shows Lucide Eye icon when hidden, and EyeOff icon when visible.
 * - Maintains independent state for each input field.
 * - Perfectly forwards refs, input props, event handlers, and data-testid attributes.
 */
const PasswordInput = forwardRef(function PasswordInput(
  {
    className = "",
    style = {},
    containerClassName = "",
    containerStyle = {},
    disabled = false,
    ...props
  },
  ref
) {
  const [showPassword, setShowPassword] = useState(false);

  const toggleVisibility = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setShowPassword((prev) => !prev);
    }
  };

  const testId = props["data-testid"] || "password-input";

  return (
    <div
      className={`password-input-wrap ${containerClassName}`.trim()}
      style={{
        position: "relative",
        width: "100%",
        display: "block",
        ...containerStyle,
      }}
    >
      <input
        ref={ref}
        type={showPassword ? "text" : "password"}
        className={className}
        disabled={disabled}
        style={{
          ...style,
          paddingRight: "40px",
        }}
        {...props}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={toggleVisibility}
        disabled={disabled}
        className="password-toggle-btn"
        aria-label={showPassword ? "Hide password" : "Show password"}
        title={showPassword ? "Hide password" : "Show password"}
        data-testid={`${testId}-toggle-btn`}
      >
        {showPassword ? (
          <EyeOff size={16} strokeWidth={2} aria-hidden="true" />
        ) : (
          <Eye size={16} strokeWidth={2} aria-hidden="true" />
        )}
      </button>
    </div>
  );
});

PasswordInput.displayName = "PasswordInput";

export default PasswordInput;
export { PasswordInput };
